import React from 'react';
import { Bookmark, Trash2 } from 'lucide-react';
import type { RackReservation } from '../../utils/rackReservation.logic';
import type { ProjectionMode } from '../equipment/RackCanvasToolbar';

interface GridReservationProps {
  res: RackReservation;
  uSlots: number;
  projectionMode: ProjectionMode;
  onClick?: (res: RackReservation) => void;
  onRelease?: (res: RackReservation) => void;
  isDark?: boolean;
}

/**
 * Renders a NetBox-style slot reservation block on the rack elevation grid.
 * Uses diagonal crosshatching pattern, CAD monospace tags, and click-to-release action.
 */
export function GridReservation({
  res,
  uSlots,
  projectionMode,
  onClick,
  onRelease,
  isDark = true,
}: GridReservationProps) {
  const u_pos = Number(res.u_start);
  const u_h = Number(res.u_height);
  const total_u = Number(uSlots);
  const rowStart = total_u - u_pos - u_h + 2;

  const resFace = (res.face || 'both').toLowerCase();

  let colStart = '2 / 3';
  if (projectionMode === 'split') {
    colStart = resFace === 'both' ? '2 / 5' : (resFace === 'front' ? '2 / 3' : '4 / 5');
  }

  // Filter visibility by projection
  if (projectionMode === 'front' && resFace === 'rear') return null;
  if (projectionMode === 'rear' && resFace === 'front') return null;

  const style: React.CSSProperties = {
    gridRow: `${rowStart} / span ${u_h}`,
    gridColumn: colStart,
    backgroundImage: isDark
      ? `repeating-linear-gradient(
          -45deg,
          rgba(245, 158, 11, 0.14) 0px,
          rgba(245, 158, 11, 0.14) 8px,
          rgba(245, 158, 11, 0.04) 8px,
          rgba(245, 158, 11, 0.04) 16px
        )`
      : `repeating-linear-gradient(
          -45deg,
          rgba(217, 119, 6, 0.15) 0px,
          rgba(217, 119, 6, 0.15) 8px,
          rgba(217, 119, 6, 0.05) 8px,
          rgba(217, 119, 6, 0.05) 16px
        )`,
  };

  const endU = u_pos + u_h - 1;
  const rangeText = u_h > 1 ? `U${u_pos}..U${endU}` : `U${u_pos}`;

  return (
    <div
      style={style}
      onClick={() => onClick?.(res)}
      title={`Зарезервировано под ${res.tenant}: ${res.description} (${rangeText}, ${u_h}U)`}
      className={`relative z-10 border border-dashed rounded-[2px] transition-all flex items-center justify-between px-2 cursor-pointer group ${
        isDark
          ? 'border-amber-500/60 hover:border-amber-400 hover:shadow-[0_0_12px_rgba(245,158,11,0.25)]'
          : 'border-amber-600/70 hover:border-amber-600 hover:shadow-[0_2px_8px_rgba(217,119,6,0.2)]'
      }`}
    >
      <div className="flex items-center gap-1.5 min-w-0 truncate select-none">
        <Bookmark size={11} className="text-amber-500 shrink-0" aria-hidden="true" />
        
        <span className="px-1 py-0.5 rounded text-[8px] font-mono font-bold tracking-wider uppercase border shrink-0 bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/40">
          РЕЗЕРВ {u_h}U
        </span>

        <span className="text-[10px] font-mono font-bold text-amber-700 dark:text-amber-300 truncate">
          {res.tenant}
        </span>

        {res.description && (
          <span className="text-[9px] font-mono text-amber-600/80 dark:text-amber-400/70 truncate hidden sm:inline">
            — {res.description}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0 pl-1">
        <span className="text-[8px] font-mono font-bold text-amber-600 dark:text-amber-500/80">
          {rangeText}
        </span>

        {onRelease && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRelease(res);
            }}
            title="Освободить зарезервированные юниты"
            className="opacity-0 group-hover:opacity-100 p-0.5 rounded text-amber-600 dark:text-amber-400/80 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-500/10 transition-opacity"
            aria-label="Освободить резерв"
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
    </div>
  );
}
